import {
    type ComponentType,
    createContext,
    useContext,
    useEffect,
    type FC
} from "react";
import type {
    StepperItemPartContentProps,
    StepperStepStateContextType,
    StepperPartContentProps,
    StepperItemContextType,
    StepperContextType
} from "./type";
import {
    warnOnce
} from "../../utils/development";

export const StepperContext = createContext<StepperContextType | undefined>(undefined);

export const StepperItemContext = createContext<StepperItemContextType | undefined>(undefined);

export const StepperStepStateContext = createContext<StepperStepStateContextType>({
    isHighlighted: false
});

export const useStepperContext = ({
    part
}: {
    part: string;
}): StepperContextType | undefined => {
    const context = useContext(StepperContext);

    useEffect(() => {
        if(context) {
            return;
        }

        warnOnce({
            message: `Stepper.${part}: must be rendered inside <Stepper>.`
        });
    }, [
        context,
        part
    ]);

    return context;
};

export const useStepperItemContext = ({
    part
}: {
    part: string;
}): StepperItemContextType | undefined => {
    const item = useContext(StepperItemContext);

    useEffect(() => {
        if(item) {
            return;
        }

        warnOnce({
            message: `Stepper.${part}: must be rendered inside <Stepper.Item>.`
        });
    }, [
        item,
        part
    ]);

    return item;
};

export const withStepperContext = <P extends object>(
    part: string,
    Content: ComponentType<StepperPartContentProps<P>>
): FC<P> => {
    const StepperPart: FC<P> = (props) => {
        const context = useStepperContext({
            part
        });

        if(!context) {
            return null;
        }

        return <Content
            {...props}
            context={context}
        />;
    };

    StepperPart.displayName = `Stepper.${part}`;

    return StepperPart;
};

export const withStepperItemContext = <P extends object>(
    part: string,
    Content: ComponentType<StepperItemPartContentProps<P>>
): FC<P> => {
    const StepperItemPart: FC<P> = (props) => {
        const context = useStepperContext({
            part
        });

        const item = useStepperItemContext({
            part
        });

        if(!context || !item) {
            return null;
        }

        return <Content
            {...props}
            context={context}
            item={item}
        />;
    };

    StepperItemPart.displayName = `Stepper.${part}`;

    return StepperItemPart;
};
